/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription, take } from 'rxjs';
// https://stackoverflow.com/questions/61591949/formatting-an-angular-material-form-field-number-to-2-decimal-places

// limits an inpit to being a number up to two dp
@Directive({
  selector: '[appTwoDigitDecimalNumber]',
  standalone: false,
})
export class TwoDigitDecimalNumberDirective implements OnInit, OnDestroy {
  @Input() applyOnInit: boolean = true;

  // Allow decimal numbers and negative values
  private regex: RegExp = new RegExp(/^\d*\.?\d{0,2}$/);
  // Allow key codes for special events. Reflect :
  // Backspace, tab, end, home
  private specialKeys: Array<string> = ['Backspace', 'Tab', 'End', 'Home', '-', 'ArrowLeft', 'ArrowRight', 'Del', 'Delete'];

  private sub!: Subscription;

  constructor(private el: ElementRef, private ngControl: NgControl) {}

  ngOnInit() {
    if (this.applyOnInit) {
      this.sub = this.ngControl.control!.valueChanges.pipe(take(1)).subscribe(() => this.applyTwoDecimals());
    }
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Allow Backspace, tab, end, and home keys
    if (this.specialKeys.indexOf(event.key) !== -1) {
      return;
    }

    const input = this.el.nativeElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;

    // remove commas so we’re testing raw numbers
    const rawValue = input.value.replace(/,/g, '');

    const nextRaw = rawValue.slice(0, start) + event.key + rawValue.slice(end);

    if (nextRaw && !this.regex.test(nextRaw)) {
      event.preventDefault();
    }
  }

  @HostListener('blur')
  onBlur() {
    this.applyTwoDecimals();
  }

  applyTwoDecimals() {
    const input = this.el.nativeElement;
    // strip commas so we have a clean number string
    const rawValue = input.value.replace(/,/g, '');

    // leave blank, "-" or "." alone
    if (!rawValue || rawValue === '-' || rawValue === '.') {
      return;
    }

    const num = parseFloat(rawValue);
    if (isNaN(num)) {
      input.value = '';
    } else {
      // always produces "1234.50" style
      input.value = num.toFixed(2);
    }

    // fire an input event so your FormatNumberInputDirective can
    // re-insert commas on the newly-padded value
    input.dispatchEvent(new Event('input'));
  }
}
